import { interpolateGradientColor, parseColor, type OklchColor } from "./color";
import { createId } from "@/lib/ids";

export interface SolidPaint {
  id: string;
  type: "solid";
  color: OklchColor;
}

export type GradientColorSpace = "rgb" | "oklch";

export interface GradientStop {
  id: string;
  position: number;
  color: OklchColor;
}

export interface GradientPoint {
  x: number;
  y: number;
}

export interface LinearGradientPaint {
  id: string;
  type: "linearGradient";
  angle: number;
  start?: GradientPoint;
  end?: GradientPoint;
  colorSpace: GradientColorSpace;
  stops: GradientStop[];
}

export interface RadialGradientPaint {
  id: string;
  type: "radialGradient";
  center?: GradientPoint;
  radiusPoint?: GradientPoint;
  radius: number;
  colorSpace: GradientColorSpace;
  stops: GradientStop[];
}

export type GradientPaint = LinearGradientPaint | RadialGradientPaint;

export type ImageFillMode = "fit" | "fill" | "crop";

export interface ImageCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageAdjustments {
  exposure: number;
  contrast: number;
  saturation: number;
  temperature: number;
  tint: number;
  highlights: number;
  shadows: number;
}

export interface ImagePaint {
  id: string;
  type: "image";
  assetId?: string;
  source?: string;
  name?: string;
  mimeType?: string;
  intrinsicWidth: number;
  intrinsicHeight: number;
  fillMode: ImageFillMode;
  crop: ImageCropRect;
  flipX?: boolean;
  flipY?: boolean;
  opacity: number;
  adjustments: ImageAdjustments;
}

export type Paint = SolidPaint | GradientPaint | ImagePaint;

/** Paint payload that may omit `id` (filled in by normalizePaint / factories). */
export type PaintDraft = (
  | Omit<SolidPaint, "id">
  | Omit<LinearGradientPaint, "id">
  | Omit<RadialGradientPaint, "id">
  | Omit<ImagePaint, "id">
) & { id?: string };

export type StrokeAlign = "inside" | "center" | "outside";

export interface StrokeStyle {
  paint: Paint;
  width: number;
  align?: StrokeAlign;
}

export interface DropShadowEffect {
  id: string;
  type: "dropShadow";
  visible: boolean;
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  color: OklchColor;
}

export interface InnerShadowEffect {
  id: string;
  type: "innerShadow";
  visible: boolean;
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  color: OklchColor;
}

export interface LayerBlurEffect {
  id: string;
  type: "layerBlur";
  visible: boolean;
  mode: "uniform" | "progressive";
  blur: number;
  start: number;
  end: number;
}

export interface BackgroundBlurEffect {
  id: string;
  type: "backgroundBlur";
  visible: boolean;
  blur: number;
}

export type NodeEffect = DropShadowEffect | InnerShadowEffect | LayerBlurEffect | BackgroundBlurEffect;

export interface NodeStyle {
  fills: Paint[];
  strokes?: StrokeStyle[];
  stroke?: StrokeStyle;
  effects?: NodeEffect[];
  opacity: number;
}

export function createSolidPaint(color: string | OklchColor = "#6ee7b7"): SolidPaint {
  return {
    id: createId("paint"),
    type: "solid",
    color: parseColor(color),
  };
}

export function createLinearGradientPaint(
  from: string | OklchColor = "#000000",
  to: string | OklchColor = "#ffffff",
): LinearGradientPaint {
  return {
    id: createId("paint"),
    type: "linearGradient",
    angle: 0,
    colorSpace: "rgb",
    stops: [
      createGradientStop(0, from),
      createGradientStop(1, to),
    ],
  };
}

export function createRadialGradientPaint(
  from: string | OklchColor = "#000000",
  to: string | OklchColor = "#ffffff",
): RadialGradientPaint {
  return {
    id: createId("paint"),
    type: "radialGradient",
    center: { x: 0.5, y: 0.5 },
    radius: 0.5,
    colorSpace: "rgb",
    stops: [
      createGradientStop(0, from),
      createGradientStop(1, to),
    ],
  };
}

export function createImagePaint({
  assetId,
  name,
  mimeType,
  intrinsicWidth,
  intrinsicHeight,
}: {
  assetId: string;
  name?: string;
  mimeType?: string;
  intrinsicWidth: number;
  intrinsicHeight: number;
}): ImagePaint {
  return normalizeImagePaint({
    type: "image",
    assetId,
    name,
    mimeType,
    intrinsicWidth,
    intrinsicHeight,
    fillMode: "fill",
    crop: { x: 0, y: 0, width: 1, height: 1 },
    opacity: 1,
    adjustments: defaultImageAdjustments(),
  });
}

export function createGradientStop(position: number, color: string | OklchColor): GradientStop {
  return {
    id: createId("stop"),
    position: clamp01(position),
    color: parseColor(color),
  };
}

export function createDropShadowEffect(): DropShadowEffect {
  return {
    id: createId("effect"),
    type: "dropShadow",
    visible: true,
    offsetX: 0,
    offsetY: 4,
    blur: 4,
    spread: 0,
    color: parseColor({ ...parseColor("#000000"), alpha: 0.25 }),
  };
}

export function createInnerShadowEffect(): InnerShadowEffect {
  return {
    id: createId("effect"),
    type: "innerShadow",
    visible: true,
    offsetX: 0,
    offsetY: 4,
    blur: 4,
    spread: 0,
    color: parseColor({ ...parseColor("#000000"), alpha: 0.25 }),
  };
}

export function createLayerBlurEffect(): LayerBlurEffect {
  return {
    id: createId("effect"),
    type: "layerBlur",
    visible: true,
    mode: "uniform",
    blur: 4,
    start: 0,
    end: 4,
  };
}

export function createBackgroundBlurEffect(): BackgroundBlurEffect {
  return {
    id: createId("effect"),
    type: "backgroundBlur",
    visible: true,
    blur: 4,
  };
}

export function normalizePaint(paint: PaintDraft | Paint): Paint {
  if (paint.type === "solid") {
    return {
      id: paint.id || createId("paint"),
      type: "solid",
      color: typeof paint.color === "string" ? parseColor(paint.color) : paint.color,
    };
  }

  if (paint.type === "image") {
    return normalizeImagePaint(paint);
  }

  if (paint.type === "linearGradient") {
    return normalizeGradientPaint({
      ...paint,
      id: paint.id || createId("paint"),
      type: "linearGradient",
    });
  }

  return normalizeGradientPaint({
    ...paint,
    id: paint.id || createId("paint"),
    type: "radialGradient",
  });
}

export function normalizeGradientPaint<T extends GradientPaint>(paint: T): T;
export function normalizeGradientPaint(paint: Omit<LinearGradientPaint, "id"> & { id?: string; type: "linearGradient" }): LinearGradientPaint;
export function normalizeGradientPaint(paint: Omit<RadialGradientPaint, "id"> & { id?: string; type: "radialGradient" }): RadialGradientPaint;
export function normalizeGradientPaint(paint: GradientPaint | (Omit<LinearGradientPaint, "id"> & { id?: string; type: "linearGradient" }) | (Omit<RadialGradientPaint, "id"> & { id?: string; type: "radialGradient" })): GradientPaint {
  const stops = paint.stops.length > 0
    ? paint.stops
    : paint.type === "radialGradient"
      ? createRadialGradientPaint().stops
      : createLinearGradientPaint().stops;

  const id = paint.id || createId("paint");
  if (paint.type === "linearGradient") {
    return {
      id,
      type: "linearGradient",
      angle: normalizeAngle(paint.angle),
      start: paint.start ? normalizeGradientPoint(paint.start) : undefined,
      end: paint.end ? normalizeGradientPoint(paint.end) : undefined,
      colorSpace: paint.colorSpace,
      stops: stops
        .map(stop => ({
          ...stop,
          id: stop.id || createId("stop"),
          position: clamp01(stop.position),
          color: typeof stop.color === "string" ? parseColor(stop.color) : stop.color,
        }))
        .sort((a, b) => a.position - b.position),
    };
  }

  return {
    id,
    type: "radialGradient",
    center: paint.center ? normalizeGradientPoint(paint.center) : { x: 0.5, y: 0.5 },
    radiusPoint: paint.radiusPoint ? normalizeGradientPoint(paint.radiusPoint) : undefined,
    radius: clampPositive(paint.radius, 0.5),
    colorSpace: paint.colorSpace,
    stops: stops
      .map(stop => ({
        ...stop,
        id: stop.id || createId("stop"),
        position: clamp01(stop.position),
        color: typeof stop.color === "string" ? parseColor(stop.color) : stop.color,
      }))
      .sort((a, b) => a.position - b.position),
  };
}

export function defaultImageAdjustments(): ImageAdjustments {
  return {
    exposure: 0,
    contrast: 0,
    saturation: 0,
    temperature: 0,
    tint: 0,
    highlights: 0,
    shadows: 0,
  };
}

export function normalizeImagePaint(paint: Omit<ImagePaint, "id"> & { id?: string }): ImagePaint {
  return {
    ...paint,
    id: paint.id || createId("paint"),
    intrinsicWidth: Math.max(1, finiteOrFallback(paint.intrinsicWidth, 1)),
    intrinsicHeight: Math.max(1, finiteOrFallback(paint.intrinsicHeight, 1)),
    fillMode: paint.fillMode ?? "fill",
    crop: normalizeCropRect(paint.crop ?? { x: 0, y: 0, width: 1, height: 1 }),
    flipX: paint.flipX ?? false,
    flipY: paint.flipY ?? false,
    opacity: clamp01(paint.opacity ?? 1),
    adjustments: {
      ...defaultImageAdjustments(),
      ...(paint.adjustments ?? {}),
    },
  };
}

export function sampleGradientPaint(paint: GradientPaint, targetStopCount = 24): GradientStop[] {
  const normalized = normalizeGradientPaint(paint);
  if (normalized.colorSpace === "rgb" || normalized.stops.length <= 1) {
    return normalized.stops;
  }

  const sampleCount = Math.max(2, targetStopCount);
  return Array.from({ length: sampleCount }, (_, index) => {
    const position = index / (sampleCount - 1);
    return {
      id: `sample_${index}`,
      position,
      color: colorAtGradientPosition(normalized, position),
    };
  });
}

export function colorAtGradientPosition(paint: GradientPaint, position: number): OklchColor {
  const normalized = normalizeGradientPaint(paint);
  const stops = normalized.stops;
  const clampedPosition = clamp01(position);
  const first = stops[0]!;
  const last = stops[stops.length - 1]!;

  if (clampedPosition <= first.position) {
    return parseColor(first.color);
  }

  if (clampedPosition >= last.position) {
    return parseColor(last.color);
  }

  const nextIndex = stops.findIndex(stop => stop.position >= clampedPosition);
  const to = stops[Math.max(1, nextIndex)]!;
  const from = stops[Math.max(0, nextIndex - 1)]!;
  const span = Math.max(0.0001, to.position - from.position);
  const t = (clampedPosition - from.position) / span;
  return interpolateGradientColor(from.color, to.color, t, normalized.colorSpace);
}

export function createDefaultStyle(fill: string | OklchColor = "#6ee7b7"): NodeStyle {
  return {
    fills: [createSolidPaint(fill)],
    strokes: [],
    stroke: undefined,
    opacity: 1,
  };
}

/** Ensures every paint in a style stack has a stable id (and normalized strokes). */
export function normalizeNodeStyle(style: NodeStyle): NodeStyle {
  const fills = normalizeFillStack(style);
  const strokes = normalizeStrokeStack(style);
  return {
    ...style,
    fills,
    strokes,
    stroke: strokes[0],
    effects: style.effects?.map(effect => (
      effect.id ? effect : { ...effect, id: createId("effect") }
    )),
  };
}

export function normalizeFillStack(style: NodeStyle): Paint[] {
  return (style.fills ?? []).map(normalizePaint);
}

export function normalizeStrokeStack(style: NodeStyle): StrokeStyle[] {
  const strokes = style.strokes ?? (style.stroke ? [style.stroke] : []);
  return strokes.map(stroke => ({
    ...stroke,
    paint: normalizePaint(stroke.paint),
  }));
}

export function styleWithStrokeStack(style: NodeStyle, strokes: StrokeStyle[]): NodeStyle {
  const normalized = strokes.map(stroke => ({
    ...stroke,
    paint: normalizePaint(stroke.paint),
  }));
  return {
    ...style,
    strokes: normalized,
    stroke: normalized[0],
  };
}

/** Content equality for paints — ignores paint (and gradient stop) identity. */
export function paintsEqual(left: Paint | undefined, right: Paint | undefined): boolean {
  if (left === right) {
    return true;
  }
  if (!left || !right || left.type !== right.type) {
    return false;
  }

  if (left.type === "solid" && right.type === "solid") {
    return JSON.stringify(left.color) === JSON.stringify(right.color);
  }

  if (left.type === "image" && right.type === "image") {
    const { id: _leftId, ...leftRest } = left;
    const { id: _rightId, ...rightRest } = right;
    return JSON.stringify(leftRest) === JSON.stringify(rightRest);
  }

  if (
    (left.type === "linearGradient" || left.type === "radialGradient")
    && left.type === right.type
  ) {
    const { id: _leftId, stops: leftStops, ...leftRest } = left;
    const { id: _rightId, stops: rightStops, ...rightRest } = right;
    if (JSON.stringify(leftRest) !== JSON.stringify(rightRest)) {
      return false;
    }
    if (leftStops.length !== rightStops.length) {
      return false;
    }
    return leftStops.every((stop, index) => {
      const other = rightStops[index]!;
      return stop.position === other.position
        && JSON.stringify(stop.color) === JSON.stringify(other.color);
    });
  }

  return false;
}

export function gradientEndpointsForBounds(
  bounds: { x: number; y: number; width: number; height: number },
  paint: LinearGradientPaint,
): { start: { x: number; y: number }; end: { x: number; y: number }; center: { x: number; y: number } } {
  const normalized = normalizeGradientPaint(paint);
  const center = {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height / 2,
  };

  if (normalized.start && normalized.end) {
    return {
      center,
      start: gradientPointToDocumentPoint(bounds, normalized.start),
      end: gradientPointToDocumentPoint(bounds, normalized.end),
    };
  }

  const radians = normalized.angle * Math.PI / 180;
  const direction = { x: Math.cos(radians), y: Math.sin(radians) };
  const radius = Math.abs(direction.x) * Math.abs(bounds.width) / 2 + Math.abs(direction.y) * Math.abs(bounds.height) / 2;
  return {
    center,
    start: { x: center.x - direction.x * radius, y: center.y - direction.y * radius },
    end: { x: center.x + direction.x * radius, y: center.y + direction.y * radius },
  };
}

export function documentPointToGradientPoint(
  bounds: { x: number; y: number; width: number; height: number },
  point: { x: number; y: number },
): GradientPoint {
  return {
    x: bounds.width === 0 ? 0.5 : (point.x - bounds.x) / bounds.width,
    y: bounds.height === 0 ? 0.5 : (point.y - bounds.y) / bounds.height,
  };
}

export function gradientPointToDocumentPoint(
  bounds: { x: number; y: number; width: number; height: number },
  point: GradientPoint,
): { x: number; y: number } {
  return {
    x: bounds.x + bounds.width * point.x,
    y: bounds.y + bounds.height * point.y,
  };
}

export function radialGradientGeometryForBounds(
  bounds: { x: number; y: number; width: number; height: number },
  paint: RadialGradientPaint,
): { center: { x: number; y: number }; radius: number } {
  const normalized = normalizeGradientPaint(paint);
  const center = gradientPointToDocumentPoint(bounds, normalized.center ?? { x: 0.5, y: 0.5 });
  return {
    center,
    radius: Math.max(0.0001, Math.max(Math.abs(bounds.width), Math.abs(bounds.height)) * normalized.radius),
  };
}

function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

function normalizeGradientPoint(point: GradientPoint): GradientPoint {
  return {
    x: Number.isFinite(point.x) ? point.x : 0.5,
    y: Number.isFinite(point.y) ? point.y : 0.5,
  };
}

function normalizeCropRect(rect: ImageCropRect): ImageCropRect {
  const width = Math.max(0.001, finiteOrFallback(rect.width, 1));
  const height = Math.max(0.001, finiteOrFallback(rect.height, 1));
  return {
    x: finiteOrFallback(rect.x, 0),
    y: finiteOrFallback(rect.y, 0),
    width,
    height,
  };
}

function clampPositive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function finiteOrFallback(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}
